import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState, useCallback } from "react";
import PortaledModal from "../../../../../components/Modals/PortaledModal";
import SetLocationMapModal, { AddressFromMap, reverseGeocode } from "../SetLocationMapModal";
import { useShippingPageContext } from "../../providers/ShippingPageProvider";
import { useCartContext } from "../../../../../customHooks/useCart";
import { useAppContext } from "../../../../../providers/AppProvider";
import { useBottomSheetAnimation } from "../../hooks/useBottomSheetAnimation";
import * as translator from "../../../../../utils/translator";
import * as http from "../../../../../utils/http";
import { validatePhone, validatePhoneDE, validateName, validateStreetAndNumber, validatePostalCode } from "../../../../../utils/fieldsValidator";
import AddressModalHeader from "./AddressModalHeader";
import LocationDetectingState from "./LocationDetectingState";
import LocationSuggestionCard from "./LocationSuggestionCard";
import AddressFormFields from "./AddressFormFields";
import AddressFormFooter from "./AddressFormFooter";
import type { NewAddressFormType, AddressToEdit, ModalPhase } from "./types";

const GERMANY_COUNTRY_ID = "DE";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSuccess: (customerAddresses: { regular: any; packstation: any; all: any[] }, meta: { isNew: boolean; usedGeoLocation: boolean }) => void;
  addressToEdit?: AddressToEdit;
  onDelete?: (addressId: number) => void;
  isMobile?: boolean;
};

const AddNewAddressModal: React.FC<Props> = ({ isOpen, onClose, onBack, onSuccess, addressToEdit = null, onDelete, isMobile = false }) => {
  const isEditMode = Boolean(addressToEdit);
  const { language, countryCurrenciesByID, isGoogleAPILoaded } = useShippingPageContext();
  const { cartCurrency } = useCartContext();
  const { setStickyNotification } = useAppContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [modalPhase, setModalPhase] = useState<ModalPhase>("form");
  const [suggestedAddress, setSuggestedAddress] = useState<AddressFromMap | null>(null);
  const [suggestedDisplayText, setSuggestedDisplayText] = useState("");
  const [usedGeoLocation, setUsedGeoLocation] = useState(false);
  const { shouldRender, isClosing } = useBottomSheetAnimation(isOpen && !showMapModal, isMobile);

  const newAddressSchema = useMemo(() => z.object({
    address_sub_type: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    zip: z.string(),
    city: z.string(),
    address: z.string(),
    country: z.string(),
    company: z.string().optional(),
    phone: z.string(),
  }).superRefine((data, ctx) => {
    const addIssue = (path: string, errorMsg: string) => {
      ctx.addIssue({ path: [path], code: z.ZodIssueCode.custom, message: errorMsg });
    };

    const firstNameResult = validateName(data.first_name, language);
    if (!firstNameResult.isValid) addIssue('first_name', firstNameResult.errorMsg);

    const lastNameResult = validateName(data.last_name, language);
    if (!lastNameResult.isValid) addIssue('last_name', lastNameResult.errorMsg);

    const zipResult = validatePostalCode(data.zip, language, data.country?.toLowerCase());
    if (!zipResult.isValid) addIssue('zip', zipResult.errorMsg);

    const cityResult = validateName(data.city, language);
    if (!cityResult.isValid) addIssue('city', cityResult.errorMsg);

    const addressResult = validateStreetAndNumber(data.address, language);
    if (!addressResult.isValid) addIssue('address', addressResult.errorMsg);

    const isGermanAddress = data.country?.toUpperCase() === GERMANY_COUNTRY_ID;
    const phoneResult = isGermanAddress
      ? validatePhoneDE(data.phone, language)
      : validatePhone(data.phone, language);
    if (!phoneResult.isValid) addIssue('phone', phoneResult.errorMsg);
  }), [language]);

  const { control, reset, handleSubmit, watch, setValue } = useForm<NewAddressFormType>({
    resolver: zodResolver(newAddressSchema),
    mode: "onChange",
    defaultValues: {
      address_sub_type: "apartment",
      first_name: "",
      last_name: "",
      zip: "",
      city: "",
      address: "",
      country: cartCurrency.countryID,
      company: "",
      phone: "",
    },
  });

  const watchedFields = watch(["first_name", "last_name", "zip", "city", "address", "phone"]);
  const allRequiredFieldsFilled = watchedFields.every((val) => val?.trim());

  const detectUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setModalPhase("form");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        reverseGeocode(latitude, longitude)
          .then((result) => {
            if (result) {
              setSuggestedAddress(result);
              const parts = [result.address, result.city].filter(Boolean);
              setSuggestedDisplayText(parts.join(", "));
              setModalPhase("suggesting");
            } else {
              setModalPhase("form");
            }
          })
          .catch(() => setModalPhase("form"));
      },
      () => setModalPhase("form"),
      { timeout: 8000, enableHighAccuracy: true }
    );
  }, []);

  useEffect(() => {
    if (isOpen && addressToEdit) {
      setModalPhase("form");
      reset({
        address_sub_type: addressToEdit.address_sub_type || "apartment",
        first_name: addressToEdit.first_name || "",
        last_name: addressToEdit.last_name || "",
        zip: addressToEdit.zip || "",
        city: addressToEdit.city || "",
        address: addressToEdit.address || "",
        country: addressToEdit.country_id || cartCurrency.countryID,
        company: addressToEdit.company || "",
        phone: addressToEdit.phone || "",
      });
    } else if (isOpen && !addressToEdit) {
      setModalPhase("detecting");
      setSuggestedAddress(null);
      setSuggestedDisplayText("");
      setUsedGeoLocation(false);
      reset({
        address_sub_type: "apartment",
        first_name: "",
        last_name: "",
        zip: "",
        city: "",
        address: "",
        country: cartCurrency.countryID,
        company: "",
        phone: "",
      });
      detectUserLocation();
    }
  }, [isOpen, addressToEdit]);

  const handleUseSuggestedAddress = useCallback(() => {
    if (!suggestedAddress) return;
    reset({
      address_sub_type: "apartment",
      first_name: "",
      last_name: "",
      zip: suggestedAddress.zip || "",
      city: suggestedAddress.city || "",
      address: suggestedAddress.address || "",
      country: cartCurrency.countryID,
      company: "",
      phone: "",
    });
    setUsedGeoLocation(true);
    setModalPhase("form");
  }, [suggestedAddress, cartCurrency.countryID, reset]);

  const handleEnterAnotherAddress = useCallback(() => {
    setModalPhase("form");
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBack = () => {
    reset();
    onBack();
  };

  const handleDeleteAddress = () => {
    if (!addressToEdit || !onDelete) return;
    setIsDeleting(true);
    http.postToServer("account-update/delivery-address/delete", {
      address_id: addressToEdit.id,
    }, [http.MIDDLEWARES.CUSTOMER_ID])
      .then(() => {
        onDelete(addressToEdit.id);
        reset();
        onClose();
      })
      .catch(() => {})
      .finally(() => setIsDeleting(false));
  };

  const handleMapSave = (addressData: AddressFromMap) => {
    setShowMapModal(false);
    reset({
      address_sub_type: "apartment",
      first_name: "",
      last_name: "",
      zip: addressData.zip || "",
      city: addressData.city || "",
      address: addressData.address || "",
      country: cartCurrency.countryID,
      company: "",
      phone: "",
    });
  };

  const handleSave = handleSubmit((formData) => {
    setIsSaving(true);
    const payload: any = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      zip: formData.zip,
      city: formData.city,
      address: formData.address,
      company: formData.company || null,
      address_sub_type: formData.address_sub_type,
      country_id: cartCurrency.countryID,
      phone: formData.phone,
    };

    if (addressToEdit?.id) {
      payload.address_id = addressToEdit.id;
    }

    http.postToServer("v2/customer-address", payload, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
      .then((response: any) => {
        onSuccess(response.data.customerAddresses, { isNew: !isEditMode, usedGeoLocation });
        reset();
        onClose();
      })
      .catch((error: any) => {
        if (error?.response?.status === 429) {
          setStickyNotification({
            show: true,
            title: translator.translate(language, "shipping", "AddNewAddressModal/rate_limit_error_title") || "Error!",
            content: translator.translate(language, "shipping", "AddNewAddressModal/rate_limit_error") || "Please wait 10 seconds before making another address change",
            level: "error",
            showInfoIcon: true,
            autoFade: true,
          });
        }
      })
      .finally(() => setIsSaving(false));
  });

  const modalTitle = isEditMode
    ? (translator.translate(language, "shipping", "AddNewAddressModal/edit_title") || "Edit address")
    : (translator.translate(language, "shipping", "AddNewAddressModal/title") || "Add new address");

  return (
    <>
      <PortaledModal
        shouldShowModal={shouldRender}
        hideModal={handleClose}
        shouldBackgroundClickHideModal={true}
      >
        <div
          id="add-address-modal"
          className={`bg-white flex flex-col overflow-hidden ${
            isMobile
              ? `fixed bottom-0 left-0 right-0 rounded-t-[30px] max-h-[95vh] will-change-transform ${
                  isClosing ? "animate-bottom-sheet-out" : "animate-bottom-sheet-in"
                }`
              : "rounded-[20px] w-[min(540px,92vw)]"
          }`}
        >
          <AddressModalHeader
            title={modalTitle}
            isMobile={isMobile}
            onBack={handleBack}
            onClose={handleClose}
          />

          {modalPhase === "detecting" && (
            <LocationDetectingState language={language} />
          )}

          {modalPhase === "suggesting" && (
            <LocationSuggestionCard
              language={language}
              displayText={suggestedDisplayText}
              onUseSuggested={handleUseSuggestedAddress}
              onEnterAnother={handleEnterAnotherAddress}
            />
          )}

          {modalPhase === "form" && (
            <>
              <AddressFormFields
                control={control}
                watch={watch}
                setValue={setValue}
                language={language}
                isMobile={isMobile}
                isEditMode={isEditMode}
                isGoogleAPILoaded={isGoogleAPILoaded}
                cartCountryID={cartCurrency.countryID}
                countryCurrenciesByID={countryCurrenciesByID}
                onOpenMap={() => setShowMapModal(true)}
              />

              <AddressFormFooter
                language={language}
                isEditMode={isEditMode}
                isSaving={isSaving}
                isDeleting={isDeleting}
                canSave={allRequiredFieldsFilled}
                onSave={handleSave}
                onDelete={handleDeleteAddress}
              />
            </>
          )}
        </div>
      </PortaledModal>

      <SetLocationMapModal
        isOpen={showMapModal}
        onClose={() => { setShowMapModal(false); onClose(); }}
        onBack={() => setShowMapModal(false)}
        onSave={handleMapSave}
        isMobile={isMobile}
      />
    </>
  );
};

export default AddNewAddressModal;
